import { useEffect, useState } from 'react'
import { Button, Checkbox, Drawer, Select } from '@mantine/core'
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_AUDIT_FILTERS,
  EVENT_RESULT_OPTIONS,
  SOURCE_OPTIONS,
  type AuditFilters,
} from './auditModel'

type Props = {
  opened: boolean
  filters: AuditFilters
  /**
   * Clinic options built from the returned events (buildClinicOptions) — passed in rather
   * than imported, because this is the one dimension whose vocabulary comes from the data
   * instead of from a fixed list.
   */
  clinicOptions: Array<{ value: string; label: string }>
  /** Roles observed in this patient's record — see buildRoleOptions. */
  roleOptions: Array<{ value: string; label: string }>
  onClose: () => void
  onApply: (filters: AuditFilters) => void
}

/**
 * "Audit filters" drawer: the six mockup dimensions (23672/23674/23675) — date range,
 * user role, event result, source, emergency-access checkbox, downloads/exports
 * checkbox — plus clinic, the seventh.
 *
 * Clinic is not in the mockups; AC8 requires it ("date, user, role, action, section,
 * clinic, source and result"), so the mockups are the layout spec and not the dimension
 * list. It filters on the `Organization/<id>` reference while showing the clinic's name;
 * see actorOrganizationReference in the middleware's audit.contract.ts for why.
 */
export function AuditFiltersDrawer({
  opened,
  filters,
  clinicOptions,
  roleOptions,
  onClose,
  onApply,
}: Props) {
  const [draft, setDraft] = useState<AuditFilters>(filters)

  // Re-seed the draft each time the drawer opens.
  useEffect(() => {
    if (opened) setDraft(filters)
  }, [opened, filters])

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      title={
        <div>
          <div className="text-xs font-semibold tracking-wide text-teal-700 uppercase">
            Refine results
          </div>
          <div className="text-xl font-semibold text-gray-900">Audit filters</div>
        </div>
      }
      padding="lg"
      size="md"
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Date range"
          data={DATE_RANGE_OPTIONS}
          value={draft.dateRange}
          onChange={(value) => value && setDraft({ ...draft, dateRange: value })}
          allowDeselect={false}
        />
        <Select
          label="User role"
          data={roleOptions}
          value={draft.userRole}
          onChange={(value) => value && setDraft({ ...draft, userRole: value })}
          allowDeselect={false}
        />
        <Select
          label="Event result"
          data={EVENT_RESULT_OPTIONS}
          value={draft.eventResult}
          onChange={(value) => value && setDraft({ ...draft, eventResult: value })}
          allowDeselect={false}
        />
        <Select
          label="Clinic / organization"
          data={clinicOptions}
          value={draft.clinic}
          onChange={(value) => value && setDraft({ ...draft, clinic: value })}
          allowDeselect={false}
        />
        <Select
          label="Source"
          data={SOURCE_OPTIONS}
          value={draft.source}
          onChange={(value) => value && setDraft({ ...draft, source: value })}
          allowDeselect={false}
        />
        <Checkbox
          label="Show downloads and exports only"
          checked={draft.downloadsOnly}
          onChange={(event) => setDraft({ ...draft, downloadsOnly: event.currentTarget.checked })}
        />
      </div>

      <div className="mt-8 flex justify-end gap-3">
        <Button
          variant="default"
          onClick={() =>
            setDraft({
              ...DEFAULT_AUDIT_FILTERS,
              // Reset touches the drawer's seven dimensions only; the inline
              // search / section bar keeps its state.
              search: draft.search,
              section: draft.section,
            })
          }
        >
          Reset
        </Button>
        <Button color="teal" onClick={() => onApply(draft)}>
          Apply filters
        </Button>
      </div>
    </Drawer>
  )
}
